import { useEffect, useRef } from "react";

/**
 * 開いているオーバーレイの記録。一番上(最後に開いたもの)だけが戻る操作で閉じる。
 * ルーターを持たないアプリなので、履歴には「オーバーレイを1枚開いた」という
 * 印だけを積む。URL は変えない。
 */
interface Entry {
  close: () => void;
  /** 戻る操作で閉じた印。自分で閉じたときとの区別に使う */
  poppedByBack: boolean;
}

const stack: Entry[] = [];

/** 自分で history.back() を呼んだ回数。その分の popstate は無視する */
let suppress = 0;
let listening = false;

function ensureListener() {
  if (listening) return;
  listening = true;
  window.addEventListener("popstate", () => {
    if (suppress > 0) {
      suppress -= 1;
      return;
    }
    const top = stack[stack.length - 1];
    if (top === undefined) return;
    top.poppedByBack = true;
    top.close();
  });
}

/**
 * スマホの戻る操作(ブラウザバック)でこのオーバーレイを閉じる。
 *
 * これが無いと、戻るジェスチャーで詳細表示ではなくアプリごと閉じてしまう。
 * 全画面オーバーレイやシートの、いちばん外側のコンポーネントで呼ぶ。
 */
export function useCloseOnBack(onClose: () => void) {
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    ensureListener();
    const entry: Entry = {
      close: () => {
        closeRef.current();
      },
      poppedByBack: false,
    };
    stack.push(entry);
    history.pushState(null, "");

    return () => {
      const index = stack.indexOf(entry);
      if (index !== -1) stack.splice(index, 1);
      // 自分で閉じたときは、積んだ履歴を自分で消す。
      // 戻る操作で閉じたときは既に消えているので触らない
      if (!entry.poppedByBack) {
        suppress += 1;
        history.back();
      }
    };
  }, []);
}
